/**
 * Cloud Gateway Page
 *
 * Página para gerenciar o Cloud Gateway (Alpine Linux) do End Customer.
 * Cada organização pode ter no máximo um gateway.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RouterIcon,
  GlobeIcon,
  ShieldIcon,
  ServerIcon,
  PlayIcon,
  SquareIcon,
  RefreshCwIcon,
  Trash2Icon,
  SettingsIcon,
  PlusIcon,
  ClockIcon,
  CpuIcon,
  ActivityIcon,
  Loader2Icon,
  AlertTriangleIcon,
  NetworkIcon,
  LockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  WifiIcon,
  WifiOffIcon,
  RadioIcon,
  PencilIcon,
  SaveIcon,
  XIcon,
  ArrowRightLeftIcon,
  FileTextIcon,
  TerminalIcon,
  MonitorIcon,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ResponsivePageHeader, ResponsiveTabs } from '@nebula/shared/components'
import { usePermissions } from '@nebula/shared/hooks'
import {
  gatewayApi,
  getGatewayStatusColor,
  getGatewayStatusLabel,
  formatUptime,
  formatPercentage,
  formatMemoryMB,
  getConnectionModeLabel,
  getConnectionModeColor,
  getGatewayConnectionStatusLabel,
  formatLastHeartbeat,
  getRDPGatewayProvisionStatusColor,
  getRDPGatewayProvisionStatusLabel,
  getRDPGatewayCertificateStatusColor,
  getRDPGatewayCertificateStatusLabel,
} from '@nebula/shared/services'
import type { CloudGatewayUpdate } from '@nebula/shared/services'
import { getErrorMessage } from '@nebula/shared/utils'

import CreateVPCModal from '../components/CreateVPCModal'
import ChangeWanModal from '../components/Gateway/ChangeWanModal'
import ConsoleTab from '../components/gateway/ConsoleTab'
import CreateGatewayModal from '../components/Gateway/CreateGatewayModal'
import DHCPLeasesTab from '../components/gateway/DHCPLeasesTab'
import FirewallRulesTab from '../components/gateway/FirewallRulesTab'
import LogsTab from '../components/gateway/LogsTab'
import PortForwardsTab from '../components/gateway/PortForwardsTab'
import VPCsTab from '../components/gateway/VPCsTab'
import { useInfraStatus } from '../hooks/useInfraStatus'

type GatewayTab =
  | 'general'
  | 'vpcs'
  | 'firewall'
  | 'port-forward'
  | 'dhcp-leases'
  | 'logs'
  | 'console'

const VALID_TABS = new Set<GatewayTab>([
  'general',
  'vpcs',
  'firewall',
  'port-forward',
  'dhcp-leases',
  'logs',
  'console',
])

export default function GatewayPage() {
  const queryClient = useQueryClient()
  const { has } = usePermissions()
  const canManageGateway = has('gateway:write')
  const infraStatus = useInfraStatus()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showChangeWanModal, setShowChangeWanModal] = useState(false)
  const [showCreateVPCModal, setShowCreateVPCModal] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [confirmAction, setConfirmAction] = useState<string | null>(null)
  // Erro da ação destrutiva em curso. Sem isto, o 400 do backend só aparecia no
  // console: o modal seguia aberto como se nada tivesse acontecido, e o cliente
  // repetia a ação achando que o clique não pegou.
  const [actionError, setActionError] = useState<string | null>(null)

  // O erro pertence à ação que o gerou. Sem zerar na troca de modal, a falha de
  // um start/stop (exibida no topo) reapareceria DENTRO do modal de deleção,
  // colada em "Tem certeza que deseja deletar a VM?" — antes de o usuário
  // confirmar nada.
  useEffect(() => {
    setActionError(null)
  }, [confirmAction])
  const [isEditing, setIsEditing] = useState(false)
  const [showRDPGWDetails, setShowRDPGWDetails] = useState(false)
  const [showRDPGWLogs, setShowRDPGWLogs] = useState(false)
  const [rdpgwLogLines, setRDPGWLogLines] = useState(100)
  const [rdpgwCommandFeedback, setRDPGWCommandFeedback] = useState<{
    kind: 'success' | 'error'
    message: string
  } | null>(null)

  const tabParam = searchParams.get('tab') as GatewayTab | null
  const activeTab: GatewayTab = tabParam && VALID_TABS.has(tabParam) ? tabParam : 'general'
  const setActiveTab = useCallback(
    (tab: GatewayTab) => {
      setSearchParams(tab === 'general' ? {} : { tab }, { replace: true })
    },
    [setSearchParams],
  )

  // Edit form state
  const [editName, setEditName] = useState('')
  const [editNat, setEditNat] = useState(false)
  const [editFirewall, setEditFirewall] = useState(false)
  const [editVpn, setEditVpn] = useState(false)
  const [editDhcp, setEditDhcp] = useState(false)
  const [editLanIp, setEditLanIp] = useState('')
  const [editDhcpRangeStart, setEditDhcpRangeStart] = useState('')
  const [editDhcpRangeEnd, setEditDhcpRangeEnd] = useState('')

  // Query para obter o gateway
  const {
    data: gateway,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['gateway'],
    queryFn: gatewayApi.getMyGateway,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 3
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'provisioning' || status === 'configuring' || status === 'rebuilding')
        return 5000
      if (status === 'running') return 30_000
      return false
    },
  })

  // Query para DHCP config
  const { data: dhcpConfig } = useQuery({
    queryKey: ['gateway-dhcp'],
    queryFn: gatewayApi.getGatewayDHCPConfig,
    enabled: !!gateway,
  })

  // Query para status em tempo real
  const { data: gatewayStatus } = useQuery({
    queryKey: ['gateway-status'],
    queryFn: gatewayApi.getGatewayStatus,
    enabled: !!gateway && gateway.status === 'running',
    refetchInterval: 30_000,
  })

  const {
    data: rdpgwStatus,
    isLoading: isLoadingRDPGWStatus,
    error: rdpgwStatusError,
  } = useQuery({
    queryKey: ['gateway-rdpgw-status'],
    queryFn: gatewayApi.getRDPGatewayStatus,
    enabled: !!gateway,
    refetchInterval: (query) => {
      const status = query.state.data?.rdpgw_provision_status
      if (status === 'provisioning') return 10_000
      if (status === 'active' || status === 'inactive' || status === 'error') return 30_000
      return false
    },
  })

  const {
    data: rdpgwLogsResult,
    isLoading: isLoadingRDPGWLogs,
    refetch: refetchRDPGWLogs,
  } = useQuery({
    queryKey: ['gateway-rdpgw-logs', rdpgwLogLines],
    queryFn: () => gatewayApi.getRDPGatewayLogs(rdpgwLogLines),
    enabled: !!rdpgwStatus?.rdpgw_enabled && showRDPGWLogs,
    refetchInterval: showRDPGWLogs ? 10_000 : false,
  })

  // Initialize edit form only when entering edit mode (not on gateway/dhcpConfig refetch)
  useEffect(() => {
    if (isEditing && gateway) {
      setEditName(gateway.name)
      setEditLanIp(gateway.lan.ip || '')
      setEditNat(gateway.features.nat)
      setEditFirewall(gateway.features.firewall)
      setEditVpn(gateway.features.vpn)
      setEditDhcp(gateway.features.dhcp_server)
      setEditDhcpRangeStart(dhcpConfig?.dhcp_range_start || '')
      setEditDhcpRangeEnd(dhcpConfig?.dhcp_range_end || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  useEffect(() => {
    if (!rdpgwCommandFeedback) return
    const timer = setTimeout(() => setRDPGWCommandFeedback(null), 8000)
    return () => clearTimeout(timer)
  }, [rdpgwCommandFeedback])

  // Mutations
  const startMutation = useMutation({
    mutationFn: gatewayApi.startGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const stopMutation = useMutation({
    mutationFn: gatewayApi.stopGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const rebootMutation = useMutation({
    mutationFn: gatewayApi.rebootGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const reconfigureMutation = useMutation({
    mutationFn: async () => {
      await gatewayApi.reconfigureGateway()
      // Re-provisionar rdpgw junto se habilitado
      if (rdpgwStatus?.rdpgw_enabled && gateway?.status === 'running') {
        await gatewayApi.reconcileRDPGateway()
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      queryClient.invalidateQueries({ queryKey: ['gateway-firewall-rules'] })
      queryClient.invalidateQueries({ queryKey: ['gateway-port-forwards'] })
      queryClient.invalidateQueries({ queryKey: ['gateway-dhcp-leases'] })
      queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-status'] })
      setActionError(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: CloudGatewayUpdate) => gatewayApi.updateGateway(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      queryClient.invalidateQueries({ queryKey: ['gateway-dhcp'] })
      setIsEditing(false)
    },
  })

  const deployMutation = useMutation({
    mutationFn: gatewayApi.deployGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const rebuildMutation = useMutation({
    mutationFn: gatewayApi.rebuildGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
      setConfirmAction(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const deleteVMMutation = useMutation({
    mutationFn: gatewayApi.deleteGatewayVM,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
      setConfirmAction(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const deleteGatewayMutation = useMutation({
    mutationFn: gatewayApi.deleteGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
      setActionError(null)
      setConfirmAction(null)
    },
    onError: (err: any) => {
      setActionError(getErrorMessage(err))
      // O backend pode ter gravado status=ERROR antes de falhar: sem isto o
      // card seguiria mostrando o estado anterior.
      queryClient.invalidateQueries({ queryKey: ['gateway'] })
    },
  })

  const initRDPGatewayMutation = useMutation({
    mutationFn: gatewayApi.initRDPGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-status'] })
    },
  })

  const reconcileRDPGatewayMutation = useMutation({
    mutationFn: gatewayApi.reconcileRDPGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-status'] })
    },
  })

  const restartRDPGatewayMutation = useMutation({
    mutationFn: gatewayApi.restartRDPGateway,
    onSuccess: (res) => {
      const check = checkGatewayCommandResult(res)
      if (check.ok) {
        setRDPGWCommandFeedback({
          kind: 'success',
          message: 'Serviço rdpgw reiniciado com sucesso.',
        })
        queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-status'] })
        queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-logs'] })
      } else {
        setRDPGWCommandFeedback({
          kind: 'error',
          message: check.error || 'O gateway não conseguiu reiniciar o rdpgw.',
        })
      }
    },
    onError: (error: any) => {
      setRDPGWCommandFeedback({
        kind: 'error',
        message: error?.response?.data?.detail || 'Falha ao reiniciar o serviço rdpgw.',
      })
    },
  })

  const handleGatewayCreated = () => {
    setShowCreateModal(false)
    queryClient.invalidateQueries({ queryKey: ['gateway'] })
  }

  const handleInitRDPGateway = () => {
    initRDPGatewayMutation.reset()
    reconcileRDPGatewayMutation.reset()
    initRDPGatewayMutation.mutate()
  }

  const handleReconcileRDPGateway = () => {
    initRDPGatewayMutation.reset()
    reconcileRDPGatewayMutation.reset()
    reconcileRDPGatewayMutation.mutate()
  }

  const handleSaveEdit = () => {
    if (!gateway) return

    const payload: CloudGatewayUpdate = {}

    if (editName !== gateway.name) payload.name = editName
    if (editLanIp !== (gateway.lan.ip || '')) payload.lan_ip = editLanIp
    if (editNat !== gateway.features.nat) payload.nat_enabled = editNat
    if (editFirewall !== gateway.features.firewall) payload.firewall_enabled = editFirewall
    if (editVpn !== gateway.features.vpn) payload.vpn_enabled = editVpn
    if (editDhcp !== gateway.features.dhcp_server) payload.dhcp_server_enabled = editDhcp

    const currentStart = dhcpConfig?.dhcp_range_start || ''
    const currentEnd = dhcpConfig?.dhcp_range_end || ''
    if (editDhcpRangeStart !== currentStart)
      payload.dhcp_range_start = editDhcpRangeStart || undefined
    if (editDhcpRangeEnd !== currentEnd) payload.dhcp_range_end = editDhcpRangeEnd || undefined

    // Only send if something changed
    if (Object.keys(payload).length === 0) {
      setIsEditing(false)
      return
    }

    updateMutation.mutate(payload)
  }

  const rdpgwProvisionStatus = rdpgwStatus?.rdpgw_provision_status || 'not_initialized'
  const rdpgwPending =
    initRDPGatewayMutation.isPending ||
    reconcileRDPGatewayMutation.isPending ||
    restartRDPGatewayMutation.isPending
  const rdpgwEnabled = !!rdpgwStatus?.rdpgw_enabled
  const rdpgwAutoShowDetails =
    rdpgwEnabled && (rdpgwProvisionStatus !== 'active' || !!rdpgwStatus?.last_error)
  const rdpgwShowDetailsPanel =
    rdpgwEnabled && (rdpgwAutoShowDetails || showRDPGWDetails || showRDPGWLogs)
  const rdpgwCanInitialize = gateway?.status === 'running' && !rdpgwStatus?.last_error
  const rdpgwCanReconcile = gateway?.status === 'running' && !!rdpgwStatus?.rdpgw_enabled

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-purple-600" />
      </div>
    )
  }

  // No gateway - show create option
  if (!gateway || (error as any)?.response?.status === 404) {
    return (
      <div className="mobile-page-padding space-y-6">
        <ResponsivePageHeader
          title="Cloud Gateway (Opcional)"
          description="Gateway de rede para sua organização"
          icon={RouterIcon}
        />

        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-purple-100">
            <RouterIcon className="size-8 text-purple-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Nenhum Gateway Configurado
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 align-middle text-xs font-normal text-gray-400">
              (Opcional)
            </span>
          </h3>
          <p className="mx-auto mb-2 max-w-md text-gray-600">
            O Cloud Gateway é um roteador virtual baseado em Alpine Linux que fornece NAT, Firewall,
            VPN e outros serviços de rede para sua infraestrutura.
          </p>
          <p className="mx-auto mb-6 max-w-md text-sm text-gray-500">
            O Gateway é opcional. Suas VMs funcionam sem ele, usando apenas a rede privada da VPC.
          </p>
          {canManageGateway ? (
            infraStatus.hasVpc ? (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 sm:w-auto"
              >
                <PlusIcon className="mr-2 size-5" />
                Criar Gateway
              </button>
            ) : (
              <div>
                <button
                  onClick={() => setShowCreateVPCModal(true)}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 sm:w-auto"
                >
                  <PlusIcon className="mr-2 size-5" />
                  Criar VPC
                </button>
                <p className="mt-2 text-xs text-amber-600">
                  Crie uma VPC primeiro para poder configurar o Gateway.
                </p>
              </div>
            )
          ) : null}
        </div>

        {/* Features info */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-green-100 p-2">
                <GlobeIcon className="size-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">NAT/SNAT</h4>
                <p className="text-sm text-gray-600">Tradução de endereços de rede</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-blue-100 p-2">
                <ShieldIcon className="size-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Firewall</h4>
                <p className="text-sm text-gray-600">Proteção de perímetro</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-purple-100 p-2">
                <LockIcon className="size-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">VPN</h4>
                <p className="text-sm text-gray-600">Conexões seguras (WireGuard)</p>
              </div>
            </div>
          </div>
        </div>

        {showCreateModal && (
          <CreateGatewayModal
            onClose={() => setShowCreateModal(false)}
            onCreated={handleGatewayCreated}
          />
        )}

        <CreateVPCModal isOpen={showCreateVPCModal} onClose={() => setShowCreateVPCModal(false)} />
      </div>
    )
  }

  // Gateway exists - show management UI
  const isLoading2 =
    startMutation.isPending ||
    stopMutation.isPending ||
    rebootMutation.isPending ||
    reconfigureMutation.isPending ||
    deployMutation.isPending ||
    rebuildMutation.isPending ||
    deleteVMMutation.isPending ||
    deleteGatewayMutation.isPending ||
    updateMutation.isPending ||
    initRDPGatewayMutation.isPending ||
    reconcileRDPGatewayMutation.isPending ||
    restartRDPGatewayMutation.isPending

  return (
    <div className="mobile-page-padding space-y-6">
      <ResponsivePageHeader
        title="Cloud Gateway"
        description="Gerenciar seu gateway de rede"
        icon={RouterIcon}
        actions={
          <div className="flex items-center gap-2">
            {!isEditing && canManageGateway && (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                <PencilIcon className="mr-1.5 size-4" />
                Editar
              </button>
            )}
            <button
              onClick={() => refetch()}
              disabled={isLoading2}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              title="Atualizar"
            >
              <RefreshCwIcon className={`size-5 ${isLoading2 ? 'animate-spin' : ''}`} />
            </button>
          </div>
        }
      />

      {/* Edit mode banner */}
      {isEditing && (
        <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center">
          <div className="flex items-center space-x-2">
            <PencilIcon className="size-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Modo de edição ativo</span>
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <button
              onClick={() => setIsEditing(false)}
              disabled={updateMutation.isPending}
              className="inline-flex flex-1 items-center justify-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-white sm:flex-none"
            >
              <XIcon className="mr-1 size-4" />
              Cancelar
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={updateMutation.isPending}
              className="inline-flex flex-1 items-center justify-center rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-purple-700 disabled:opacity-50 sm:flex-none"
            >
              {updateMutation.isPending ? (
                <Loader2Icon className="mr-1 size-4 animate-spin" />
              ) : (
                <SaveIcon className="mr-1 size-4" />
              )}
              Salvar
            </button>
          </div>
        </div>
      )}

      {/* Erro de ação sem modal (start/stop/reboot). Com o modal aberto, a
          mensagem aparece lá dentro, junto do botão que a disparou. */}
      {actionError && !confirmAction && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {/* Update error */}
      {updateMutation.isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getErrorMessage(updateMutation.error)}
        </div>
      )}

      {/* Status Card */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-center space-x-4">
              <div
                className={`rounded-xl p-3 ${
                  gateway.status === 'running'
                    ? 'bg-green-100'
                    : gateway.status === 'stopped'
                      ? 'bg-gray-100'
                      : gateway.status === 'error'
                        ? 'bg-red-100'
                        : 'bg-blue-100'
                }`}
              >
                <RouterIcon
                  className={`size-8 ${
                    gateway.status === 'running'
                      ? 'text-green-600'
                      : gateway.status === 'stopped'
                        ? 'text-gray-600'
                        : gateway.status === 'error'
                          ? 'text-red-600'
                          : 'text-blue-600'
                  }`}
                />
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xl font-semibold text-gray-900 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                  />
                ) : (
                  <h2 className="text-xl font-semibold text-gray-900">{gateway.name}</h2>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getGatewayStatusColor(gateway.status)}`}
                  >
                    {getGatewayStatusLabel(gateway.status)}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getConnectionModeColor(gateway.connection_mode, gateway.is_connected)}`}
                  >
                    {gateway.is_connected ? (
                      <WifiIcon className="mr-1 size-3" />
                    ) : (
                      <WifiOffIcon className="mr-1 size-3" />
                    )}
                    {getGatewayConnectionStatusLabel(gateway.connection_mode, gateway.is_connected)}
                  </span>
                </div>
                {gateway.status_message && gateway.status !== 'running' && (
                  <p className="mt-1 text-sm text-gray-600">{gateway.status_message}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            {!isEditing && canManageGateway && (
              <div className="flex flex-wrap items-center gap-2">
                {gateway.status === 'pending' && !gateway.has_vm && (
                  <button
                    onClick={() => deployMutation.mutate()}
                    disabled={isLoading2}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 disabled:opacity-50 sm:w-auto"
                  >
                    {deployMutation.isPending ? (
                      <Loader2Icon className="mr-2 size-4 animate-spin" />
                    ) : (
                      <PlayIcon className="mr-2 size-4" />
                    )}
                    Deploy VM
                  </button>
                )}
                {(gateway.status === 'provisioning' || gateway.status === 'configuring') && (
                  <div className="flex items-center space-x-2 rounded-lg bg-blue-50 px-4 py-2 text-blue-700">
                    <Loader2Icon className="size-4 animate-spin" />
                    <span className="text-sm font-medium">
                      {gateway.status === 'provisioning'
                        ? 'Provisionando VM...'
                        : 'Configurando Gateway...'}
                    </span>
                  </div>
                )}
                {gateway.status === 'rebuilding' && (
                  <div className="flex items-center space-x-2 rounded-lg bg-amber-50 px-4 py-2 text-amber-700">
                    <Loader2Icon className="size-4 animate-spin" />
                    <span className="text-sm font-medium">Recriando VM...</span>
                  </div>
                )}
                {gateway.status === 'stopped' && (
                  <button
                    onClick={() => startMutation.mutate()}
                    disabled={isLoading2}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:opacity-50 sm:w-auto"
                  >
                    {startMutation.isPending ? (
                      <Loader2Icon className="mr-2 size-4 animate-spin" />
                    ) : (
                      <PlayIcon className="mr-2 size-4" />
                    )}
                    Iniciar
                  </button>
                )}
                {gateway.status === 'running' && (
                  <>
                    <button
                      onClick={() => stopMutation.mutate()}
                      disabled={isLoading2}
                      className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
                    >
                      {stopMutation.isPending ? (
                        <Loader2Icon className="mr-2 size-4 animate-spin" />
                      ) : (
                        <SquareIcon className="mr-2 size-4" />
                      )}
                      Parar
                    </button>
                    <button
                      onClick={() => rebootMutation.mutate()}
                      disabled={isLoading2}
                      className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
                    >
                      {rebootMutation.isPending ? (
                        <Loader2Icon className="mr-2 size-4 animate-spin" />
                      ) : (
                        <RefreshCwIcon className="mr-2 size-4" />
                      )}
                      Reiniciar
                    </button>
                    <button
                      onClick={() => reconfigureMutation.mutate()}
                      disabled={isLoading2}
                      className="inline-flex w-full items-center justify-center rounded-lg border border-blue-300 px-3 py-2 text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 sm:w-auto"
                    >
                      {reconfigureMutation.isPending ? (
                        <Loader2Icon className="mr-2 size-4 animate-spin" />
                      ) : (
                        <SettingsIcon className="mr-2 size-4" />
                      )}
                      Reaplicar Config
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Real-time stats (when running) */}
          {gateway.status === 'running' && gatewayStatus && (
            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-100 pt-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="text-center">
                <div className="mb-1 flex items-center justify-center space-x-1 text-gray-500">
                  <ClockIcon className="size-4" />
                  <span className="text-xs">Uptime</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatUptime(gatewayStatus.uptime)}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-1 flex items-center justify-center space-x-1 text-gray-500">
                  <CpuIcon className="size-4" />
                  <span className="text-xs">CPU</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatPercentage(gatewayStatus.cpu_usage)}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-1 flex items-center justify-center space-x-1 text-gray-500">
                  <ActivityIcon className="size-4" />
                  <span className="text-xs">Memória</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatPercentage(gatewayStatus.memory_usage)}
                </p>
              </div>
              <div className="text-center">
                <div className="mb-1 flex items-center justify-center space-x-1 text-gray-500">
                  <GlobeIcon className="size-4" />
                  <span className="text-xs">WAN IP</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {gatewayStatus.wan_ip_current || '-'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <ResponsiveTabs<GatewayTab>
        tabs={[
          { id: 'general', label: 'Visão Geral', icon: ActivityIcon },
          { id: 'vpcs', label: 'VPCs', icon: NetworkIcon },
          {
            id: 'firewall',
            label: 'Firewall',
            icon: ShieldIcon,
            disabled: gateway.status !== 'running',
          },
          {
            id: 'port-forward',
            label: 'Port Forward',
            icon: ArrowRightLeftIcon,
            disabled: gateway.status !== 'running',
          },
          {
            id: 'dhcp-leases',
            label: 'DHCP Leases',
            icon: ServerIcon,
            disabled: gateway.status !== 'running',
          },
          { id: 'logs', label: 'Logs', icon: FileTextIcon, disabled: gateway.status !== 'running' },
          {
            id: 'console',
            label: 'Console',
            icon: TerminalIcon,
            disabled: gateway.status !== 'running',
          },
        ]}
        value={activeTab}
        onChange={(tabId) => setActiveTab(tabId)}
        ariaLabel="Cloud Gateway"
      />

      {activeTab !== 'general' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          {activeTab === 'vpcs' && <VPCsTab gatewayVnetId={gateway.lan.vnet_id ?? undefined} />}
          {activeTab === 'firewall' && <FirewallRulesTab />}
          {activeTab === 'port-forward' && <PortForwardsTab />}
          {activeTab === 'dhcp-leases' && <DHCPLeasesTab />}
          {activeTab === 'logs' && <LogsTab />}
          {activeTab === 'console' && (
            <ConsoleTab
              initialUser={gateway.initial_user}
              initialPassword={gateway.initial_password}
            />
          )}
        </div>
      )}

      {/* Configuration Cards (General tab) */}
      {activeTab === 'general' && (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* WAN Configuration */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center space-x-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <GlobeIcon className="size-5 text-blue-600" />
                </div>
                <h3 className="flex-1 text-lg font-semibold text-gray-900">WAN (Internet)</h3>
                {canManageGateway &&
                  // Backend recusa troca de WAN nesses status (concorreria com a
                  // task de deploy/exclusão) — não oferecer o que dá 400.
                  !['provisioning', 'rebuilding', 'deleting'].includes(gateway.status) && (
                    <button
                      onClick={() => setShowChangeWanModal(true)}
                      className="text-sm font-medium text-purple-600 hover:text-purple-700"
                    >
                      Alterar
                    </button>
                  )}
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Interface</dt>
                  <dd className="text-sm font-medium text-gray-900">{gateway.wan.interface}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Modo</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {gateway.wan.mode === 'pool'
                      ? 'IP público do provedor'
                      : gateway.wan.dhcp
                        ? 'DHCP'
                        : 'Estático'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">IP</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {gateway.wan.ip || <span className="text-gray-400">-</span>}
                  </dd>
                </div>
                {!gateway.wan.dhcp && gateway.wan.netmask && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Máscara</dt>
                    <dd className="text-sm font-medium text-gray-900">{gateway.wan.netmask}</dd>
                  </div>
                )}
                {!gateway.wan.dhcp && gateway.wan.gateway && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Gateway</dt>
                    <dd className="text-sm font-medium text-gray-900">{gateway.wan.gateway}</dd>
                  </div>
                )}
                {gateway.wan.dns.length > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">DNS</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {gateway.wan.dns.join(', ')}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* LAN Configuration */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center space-x-3">
                <div className="rounded-lg bg-purple-100 p-2">
                  <NetworkIcon className="size-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">LAN (Rede Interna)</h3>
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Interface</dt>
                  <dd className="text-sm font-medium text-gray-900">{gateway.lan.interface}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-gray-600">IP do Gateway</dt>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editLanIp}
                      onChange={(e) => setEditLanIp(e.target.value)}
                      placeholder="Ex: 10.0.0.1"
                      className="w-40 rounded-lg border border-gray-300 px-2 py-1 text-right text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                    />
                  ) : (
                    <dd className="text-sm font-medium text-gray-900">{gateway.lan.ip || '-'}</dd>
                  )}
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Rede</dt>
                  <dd className="text-sm font-medium text-gray-900">{gateway.lan.cidr || '-'}</dd>
                </div>
                {gateway.lan.vnet_id && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">VNET ID</dt>
                    <dd className="text-sm font-medium text-gray-900">{gateway.lan.vnet_id}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Features */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Funcionalidades</h3>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                <FeatureToggle
                  icon={GlobeIcon}
                  label="NAT"
                  enabled={editNat}
                  onChange={setEditNat}
                />
                <FeatureToggle
                  icon={ShieldIcon}
                  label="Firewall"
                  enabled={editFirewall}
                  onChange={setEditFirewall}
                />
                <FeatureToggle
                  icon={LockIcon}
                  label="VPN"
                  enabled={editVpn}
                  onChange={setEditVpn}
                />
                <FeatureCard
                  icon={ActivityIcon}
                  label="Load Balancer"
                  enabled={gateway.features.loadbalancer}
                />
                <FeatureToggle
                  icon={ServerIcon}
                  label="DHCP Server"
                  enabled={editDhcp}
                  onChange={setEditDhcp}
                />
                <FeatureStatusCard
                  icon={MonitorIcon}
                  label="RDP Gateway"
                  status={getRDPGatewayProvisionStatusLabel(rdpgwProvisionStatus)}
                  tone={getRDPGatewayFeatureTone(rdpgwProvisionStatus)}
                  onClick={rdpgwCanInitialize ? handleInitRDPGateway : undefined}
                  disabled={!rdpgwCanInitialize || rdpgwPending}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                <FeatureCard icon={GlobeIcon} label="NAT" enabled={gateway.features.nat} />
                <FeatureCard
                  icon={ShieldIcon}
                  label="Firewall"
                  enabled={gateway.features.firewall}
                />
                <FeatureCard icon={LockIcon} label="VPN" enabled={gateway.features.vpn} />
                <FeatureCard
                  icon={ActivityIcon}
                  label="Load Balancer"
                  enabled={gateway.features.loadbalancer}
                />
                <FeatureCard
                  icon={ServerIcon}
                  label="DHCP Server"
                  enabled={gateway.features.dhcp_server}
                />
                <FeatureStatusCard
                  icon={MonitorIcon}
                  label="RDP Gateway"
                  status={getRDPGatewayProvisionStatusLabel(rdpgwProvisionStatus)}
                  tone={getRDPGatewayFeatureTone(rdpgwProvisionStatus)}
                  onClick={
                    rdpgwEnabled
                      ? rdpgwAutoShowDetails
                        ? undefined
                        : () => {
                            setShowRDPGWDetails((current) => {
                              const next = !current
                              if (!next) {
                                setShowRDPGWLogs(false)
                              }
                              return next
                            })
                          }
                      : rdpgwCanInitialize
                        ? handleInitRDPGateway
                        : undefined
                  }
                  disabled={rdpgwEnabled ? rdpgwPending : !rdpgwCanInitialize || rdpgwPending}
                />
              </div>
            )}

            {rdpgwEnabled && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <>
                  <button
                    onClick={() =>
                      queryClient.invalidateQueries({ queryKey: ['gateway-rdpgw-status'] })
                    }
                    disabled={rdpgwPending}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCwIcon
                      className={`mr-2 size-4 ${rdpgwPending ? 'animate-spin' : ''}`}
                    />
                    Atualizar Status
                  </button>
                  {!rdpgwAutoShowDetails && (
                    <button
                      onClick={() => {
                        setShowRDPGWDetails((current) => {
                          const next = !current
                          if (!next) {
                            setShowRDPGWLogs(false)
                          }
                          return next
                        })
                      }}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      {showRDPGWDetails ? (
                        <ChevronUpIcon className="mr-2 size-4" />
                      ) : (
                        <ChevronDownIcon className="mr-2 size-4" />
                      )}
                      {showRDPGWDetails ? 'Ocultar Detalhes' : 'Ver Detalhes'}
                    </button>
                  )}
                </>
              </div>
            )}

            <div className="mt-4 space-y-3">
              {rdpgwStatusError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {(rdpgwStatusError as any)?.response?.data?.detail ||
                    'Não foi possível consultar o status do RDP Gateway.'}
                </div>
              )}

              {initRDPGatewayMutation.isSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {initRDPGatewayMutation.data.message}
                </div>
              )}

              {reconcileRDPGatewayMutation.isSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {reconcileRDPGatewayMutation.data.message}
                </div>
              )}

              {(initRDPGatewayMutation.isError || reconcileRDPGatewayMutation.isError) && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {(initRDPGatewayMutation.error as any)?.response?.data?.detail ||
                    (reconcileRDPGatewayMutation.error as any)?.response?.data?.detail ||
                    'Falha ao processar o RDP Gateway.'}
                </div>
              )}

              {rdpgwCommandFeedback && (
                <div
                  className={`rounded-lg border px-4 py-3 text-sm ${
                    rdpgwCommandFeedback.kind === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}
                >
                  {rdpgwCommandFeedback.message}
                </div>
              )}

              {isLoadingRDPGWStatus && !rdpgwStatus && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Loader2Icon className="size-4 animate-spin" />
                  <span>Consultando status do RDP Gateway...</span>
                </div>
              )}

              {!rdpgwEnabled && rdpgwStatus?.last_error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {rdpgwStatus.last_error}
                </div>
              )}

              {!rdpgwEnabled && !rdpgwStatus?.last_error && gateway.status !== 'running' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  O gateway precisa estar em execução antes de habilitar o RDP Gateway.
                </div>
              )}

              {!rdpgwEnabled &&
                !rdpgwStatus?.last_error &&
                gateway.status === 'running' &&
                !gateway.is_connected && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                    O provisionamento pode ser inicializado agora, mas a instalação no agente só
                    conclui quando o gateway estiver conectado ao SaaS.
                  </div>
                )}
            </div>
          </div>

          {rdpgwShowDetailsPanel && rdpgwStatus && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">RDP Gateway</h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setShowRDPGWLogs((current) => !current)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <FileTextIcon className="mr-2 size-4" />
                    {showRDPGWLogs ? 'Ocultar Logs' : 'Ver Logs'}
                  </button>
                  <button
                    onClick={() => restartRDPGatewayMutation.mutate()}
                    disabled={rdpgwPending}
                    className="inline-flex items-center justify-center rounded-lg border border-amber-300 px-3 py-2 text-sm text-amber-800 transition-colors hover:bg-amber-50 disabled:opacity-50"
                  >
                    <RefreshCwIcon
                      className={`mr-2 size-4 ${restartRDPGatewayMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    Reiniciar Serviço
                  </button>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRDPGatewayProvisionStatusColor(rdpgwProvisionStatus)}`}
                  >
                    {getRDPGatewayProvisionStatusLabel(rdpgwProvisionStatus)}
                  </span>
                  {rdpgwStatus.cert_status && (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRDPGatewayCertificateStatusColor(rdpgwStatus.cert_status)}`}
                    >
                      Certificado: {getRDPGatewayCertificateStatusLabel(rdpgwStatus.cert_status)}
                    </span>
                  )}
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                    Fonte:{' '}
                    {rdpgwStatus.status_source === 'database+agent' ? 'Banco + Agent' : 'Banco'}
                  </span>
                </div>

                <dl className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-3">
                  <div className="rounded-lg border border-gray-200 px-4 py-3">
                    <dt className="text-xs tracking-wide text-gray-500 uppercase">
                      Expiração do Certificado
                    </dt>
                    <dd className="mt-1 text-sm font-medium text-gray-900">
                      {formatGatewayDateTime(rdpgwStatus.rdpgw_cert_expires_at)}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-gray-200 px-4 py-3">
                    <dt className="text-xs tracking-wide text-gray-500 uppercase">
                      Conectividade do Gateway
                    </dt>
                    <dd
                      className={`mt-1 text-sm font-medium ${rdpgwStatus.gateway_connected ? 'text-green-700' : 'text-gray-600'}`}
                    >
                      {rdpgwStatus.gateway_connected ? 'Conectado' : 'Desconectado'}
                    </dd>
                  </div>
                  <div className="rounded-lg border border-gray-200 px-4 py-3">
                    <dt className="text-xs tracking-wide text-gray-500 uppercase">Status Live</dt>
                    <dd className="mt-1 text-sm font-medium text-gray-900">
                      {rdpgwStatus.live_status_available ? 'Disponível' : 'Indisponível'}
                    </dd>
                  </div>
                </dl>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <DiagnosticChip label="Binário Instalado" value={rdpgwStatus.service_installed} />
                  <DiagnosticChip label="Serviço em Execução" value={rdpgwStatus.service_running} />
                  <DiagnosticChip label="Configuração Gravada" value={rdpgwStatus.config_exists} />
                </div>

                {rdpgwStatus.status_message && (
                  <div
                    className={`rounded-lg border px-4 py-3 text-sm ${
                      rdpgwProvisionStatus === 'active'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : rdpgwProvisionStatus === 'error'
                          ? 'border-red-200 bg-red-50 text-red-700'
                          : 'border-blue-200 bg-blue-50 text-blue-700'
                    }`}
                  >
                    <div>{rdpgwStatus.status_message}</div>
                    {rdpgwStatus.recommended_action && (
                      <div className="mt-2 font-medium">
                        Próximo passo: {rdpgwStatus.recommended_action}
                      </div>
                    )}
                  </div>
                )}

                {rdpgwStatus.last_error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {rdpgwStatus.last_error}
                  </div>
                )}

                {rdpgwProvisionStatus !== 'active' && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="text-sm text-amber-900">
                        O RDP Gateway ainda não está pronto para uso. Você pode reenviar o
                        provisionamento para forçar instalação, emissão de certificado e restart do
                        serviço.
                      </div>
                      <button
                        onClick={handleReconcileRDPGateway}
                        disabled={!rdpgwCanReconcile || rdpgwPending}
                        className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                      >
                        {reconcileRDPGatewayMutation.isPending ? (
                          <Loader2Icon className="mr-2 size-4 animate-spin" />
                        ) : (
                          <RefreshCwIcon className="mr-2 size-4" />
                        )}
                        Tentar Novamente
                      </button>
                    </div>
                  </div>
                )}

                {showRDPGWLogs && (
                  <div className="rounded-lg border border-gray-200">
                    <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Logs do rdpgw</h4>
                        <p className="text-xs text-gray-500">
                          Saída combinada dos arquivos <code className="font-mono">rdpgw.log</code>{' '}
                          e <code className="font-mono">rdpgw.err</code>.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={rdpgwLogLines}
                          onChange={(e) => setRDPGWLogLines(Number(e.target.value))}
                          className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                        >
                          {[50, 100, 200, 500].map((n) => (
                            <option key={n} value={n}>
                              {n} linhas
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => refetchRDPGWLogs()}
                          disabled={isLoadingRDPGWLogs}
                          className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                        >
                          <RefreshCwIcon
                            className={`mr-2 size-4 ${isLoadingRDPGWLogs ? 'animate-spin' : ''}`}
                          />
                          Atualizar Logs
                        </button>
                      </div>
                    </div>

                    {rdpgwLogsResult?.error ? (
                      <div className="border-t border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {rdpgwLogsResult.error}
                      </div>
                    ) : (rdpgwLogsResult?.result?.logs?.length || 0) === 0 ? (
                      <div className="px-4 py-6 text-sm text-gray-500">
                        Nenhum log do rdpgw disponível no momento.
                      </div>
                    ) : (
                      <div className="max-h-[320px] overflow-auto bg-gray-950 px-4 py-3 font-mono text-xs leading-6 text-gray-100">
                        {(rdpgwLogsResult?.result?.logs as string[]).map((line, index) => (
                          <div
                            key={`${index}-${line.slice(0, 32)}`}
                            className="break-all whitespace-pre-wrap"
                          >
                            {line}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DHCP Configuration */}
          {(gateway.features.dhcp_server || (isEditing && editDhcp)) && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center space-x-3">
                <div className="rounded-lg bg-teal-100 p-2">
                  <ServerIcon className="size-5 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Configuração DHCP</h3>
              </div>
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        IP Inicial do Range
                      </label>
                      <input
                        type="text"
                        value={editDhcpRangeStart}
                        onChange={(e) => setEditDhcpRangeStart(e.target.value)}
                        placeholder="Ex: 10.0.0.100"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        IP Final do Range
                      </label>
                      <input
                        type="text"
                        value={editDhcpRangeEnd}
                        onChange={(e) => setEditDhcpRangeEnd(e.target.value)}
                        placeholder="Ex: 10.0.0.200"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                  {dhcpConfig && (
                    <p className="text-xs text-gray-500">
                      Subnet: {dhcpConfig.subnet_cidr || '-'} | Gateway:{' '}
                      {dhcpConfig.gateway_ip || '-'}
                    </p>
                  )}
                  {!dhcpConfig?.has_subnet && (
                    <div className="flex items-center space-x-2 rounded-lg bg-amber-50 p-3 text-amber-700">
                      <AlertTriangleIcon className="size-4 shrink-0" />
                      <span className="text-sm">
                        Nenhuma subnet configurada na VPC. Configure primeiro na aba IPAM.
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Range Inicial</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {dhcpConfig?.dhcp_range_start || (
                        <span className="text-amber-600">Não configurado</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Range Final</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {dhcpConfig?.dhcp_range_end || (
                        <span className="text-amber-600">Não configurado</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Gateway IP</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {dhcpConfig?.gateway_ip || '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Subnet</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {dhcpConfig?.subnet_cidr || '-'}
                    </dd>
                  </div>
                  {!dhcpConfig?.dhcp_range_start && !dhcpConfig?.dhcp_range_end && (
                    <div className="mt-2 flex items-center space-x-2 rounded-lg bg-amber-50 p-3 text-amber-700">
                      <AlertTriangleIcon className="size-4 shrink-0" />
                      <span className="text-sm">
                        Range DHCP não configurado. Clique em "Editar" para configurar.
                      </span>
                    </div>
                  )}
                </dl>
              )}
            </div>
          )}

          {/* Specs & Advanced */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <SettingsIcon className="size-5 text-gray-400" />
                <span className="font-medium text-gray-900">Especificações & Avançado</span>
              </div>
              {showAdvanced ? (
                <ChevronUpIcon className="size-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="size-5 text-gray-400" />
              )}
            </button>

            {showAdvanced && (
              <div className="border-t border-gray-100 px-6 pt-4 pb-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  {/* Specs */}
                  <div>
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Especificações da VM</h4>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">CPU</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {gateway.specs.cpu_cores} cores
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Memória</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {formatMemoryMB(gateway.specs.memory_mb)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Storage</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {gateway.specs.storage_gb} GB
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Connection Info */}
                  <div>
                    <h4 className="mb-3 text-sm font-medium text-gray-700">Conexão com SaaS</h4>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Modo</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          <span className="inline-flex items-center">
                            <RadioIcon className="mr-1 size-3" />
                            {getConnectionModeLabel(gateway.connection_mode)}
                          </span>
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Status</dt>
                        <dd
                          className={`text-sm font-medium ${gateway.is_connected ? 'text-green-600' : 'text-gray-500'}`}
                        >
                          {gateway.is_connected ? 'Conectado' : 'Desconectado'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Último Heartbeat</dt>
                        <dd className="text-sm font-medium text-gray-900">
                          {formatLastHeartbeat(gateway.last_heartbeat_at)}
                        </dd>
                      </div>
                      {gateway.connection_mode === 'relay' && gateway.mgmt_ip && (
                        <div className="flex justify-between">
                          <dt className="text-sm text-gray-600">IP Gerenciamento</dt>
                          <dd className="text-sm font-medium text-gray-900">{gateway.mgmt_ip}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Danger Zone */}
                  <div>
                    <h4 className="mb-3 text-sm font-medium text-red-700">Zona de Perigo</h4>
                    <div className="space-y-2">
                      {canManageGateway && gateway.has_vm && (
                        <button
                          onClick={() => setConfirmAction('rebuild')}
                          disabled={isLoading2 || gateway.status === 'rebuilding'}
                          className="flex w-full items-center justify-between rounded-lg border border-amber-300 px-4 py-2 text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50"
                        >
                          <span className="text-sm">Rebuild (recriar VM)</span>
                          <RefreshCwIcon className="size-4" />
                        </button>
                      )}
                      {canManageGateway && gateway.has_vm && (
                        <button
                          onClick={() => setConfirmAction('delete-vm')}
                          disabled={isLoading2}
                          className="flex w-full items-center justify-between rounded-lg border border-orange-300 px-4 py-2 text-orange-700 transition-colors hover:bg-orange-50 disabled:opacity-50"
                        >
                          <span className="text-sm">Deletar VM (manter configuração)</span>
                          <Trash2Icon className="size-4" />
                        </button>
                      )}
                      {canManageGateway && (
                        <button
                          onClick={() => setConfirmAction('delete-gateway')}
                          disabled={isLoading2 || gateway.has_vm}
                          className="flex w-full items-center justify-between rounded-lg border border-red-300 px-4 py-2 text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
                          title={gateway.has_vm ? 'Delete a VM primeiro' : undefined}
                        >
                          <span className="text-sm">Deletar Gateway</span>
                          <Trash2Icon className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="mobile-modal-surface mx-4 w-full max-w-md rounded-xl bg-white p-6">
            <div className="mb-4 flex items-center space-x-3">
              <div
                className={`rounded-full p-2 ${confirmAction === 'rebuild' ? 'bg-amber-100' : 'bg-red-100'}`}
              >
                {confirmAction === 'rebuild' ? (
                  <RefreshCwIcon className="size-6 text-amber-600" />
                ) : (
                  <AlertTriangleIcon className="size-6 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {confirmAction === 'rebuild' ? 'Rebuild Gateway' : 'Confirmar Ação'}
              </h3>
            </div>
            <p className="mb-6 text-gray-600">
              {confirmAction === 'rebuild'
                ? 'A VM do gateway será destruída e recriada do zero. Todas as configurações (firewall, port forwards, rotas estáticas, DHCP) serão preservadas e reaplicadas automaticamente.'
                : confirmAction === 'delete-vm'
                  ? 'Tem certeza que deseja deletar a VM? A configuração do gateway será mantida e você poderá fazer deploy novamente.'
                  : 'Tem certeza que deseja deletar o gateway? Esta ação não pode ser desfeita.'}
            </p>
            {actionError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {actionError}
              </div>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
              <button
                onClick={() => {
                  setActionError(null)
                  setConfirmAction(null)
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setActionError(null)
                  if (confirmAction === 'rebuild') {
                    rebuildMutation.mutate()
                  } else if (confirmAction === 'delete-vm') {
                    deleteVMMutation.mutate()
                  } else {
                    deleteGatewayMutation.mutate()
                  }
                }}
                disabled={
                  rebuildMutation.isPending ||
                  deleteVMMutation.isPending ||
                  deleteGatewayMutation.isPending
                }
                className={`rounded-lg px-4 py-2 text-white transition-colors disabled:opacity-50 ${
                  confirmAction === 'rebuild'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {rebuildMutation.isPending ||
                deleteVMMutation.isPending ||
                deleteGatewayMutation.isPending ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateGatewayModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleGatewayCreated}
        />
      )}

      {/* Change WAN Modal */}
      {showChangeWanModal && gateway && (
        <ChangeWanModal
          gateway={gateway}
          onClose={() => setShowChangeWanModal(false)}
          onChanged={() => {
            setShowChangeWanModal(false)
            refetch()
          }}
        />
      )}
    </div>
  )
}

// Feature Card Component (read-only)
function FeatureCard({
  icon: Icon,
  label,
  enabled,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  enabled: boolean
}) {
  return (
    <div
      className={`flex items-center space-x-3 rounded-lg p-3 ${
        enabled ? 'bg-green-50' : 'bg-gray-50'
      }`}
    >
      <div className={`rounded-lg p-2 ${enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
        <Icon className={`size-4 ${enabled ? 'text-green-600' : 'text-gray-400'}`} />
      </div>
      <div>
        <p className={`text-sm font-medium ${enabled ? 'text-green-900' : 'text-gray-500'}`}>
          {label}
        </p>
        <p className={`text-xs ${enabled ? 'text-green-600' : 'text-gray-400'}`}>
          {enabled ? 'Ativo' : 'Inativo'}
        </p>
      </div>
    </div>
  )
}

function FeatureStatusCard({
  icon: Icon,
  label,
  status,
  tone,
  onClick,
  disabled = false,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  status: string
  tone: 'active' | 'inactive' | 'warning' | 'error'
  onClick?: () => void
  disabled?: boolean
}) {
  const toneClasses = {
    active: {
      container: 'bg-green-50',
      iconWrap: 'bg-green-100',
      icon: 'text-green-600',
      label: 'text-green-900',
      status: 'text-green-600',
    },
    inactive: {
      container: 'bg-gray-50',
      iconWrap: 'bg-gray-200',
      icon: 'text-gray-400',
      label: 'text-gray-700',
      status: 'text-gray-500',
    },
    warning: {
      container: 'bg-amber-50',
      iconWrap: 'bg-amber-100',
      icon: 'text-amber-600',
      label: 'text-amber-900',
      status: 'text-amber-700',
    },
    error: {
      container: 'bg-red-50',
      iconWrap: 'bg-red-100',
      icon: 'text-red-600',
      label: 'text-red-900',
      status: 'text-red-700',
    },
  } as const

  const classes = toneClasses[tone]
  const className = `flex items-center space-x-3 rounded-lg p-3 ${classes.container} ${
    onClick ? 'transition-colors' : ''
  } ${onClick && !disabled ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-sky-200' : ''} ${
    disabled ? 'opacity-60 cursor-not-allowed' : ''
  }`
  const content = (
    <>
      <div className={`rounded-lg p-2 ${classes.iconWrap}`}>
        <Icon className={`size-4 ${classes.icon}`} />
      </div>
      <div>
        <p className={`text-sm font-medium ${classes.label}`}>{label}</p>
        <p className={`text-xs ${classes.status}`}>{status}</p>
      </div>
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={className}>
        {content}
      </button>
    )
  }

  return <div className={className}>{content}</div>
}

// Feature Toggle Component (edit mode)
function FeatureToggle({
  icon: Icon,
  label,
  enabled,
  onChange,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  enabled: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`flex cursor-pointer items-center space-x-3 rounded-lg border-2 p-3 transition-colors ${
        enabled
          ? 'border-green-300 bg-green-50 hover:border-green-400'
          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
      }`}
    >
      <div className={`rounded-lg p-2 ${enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
        <Icon className={`size-4 ${enabled ? 'text-green-600' : 'text-gray-400'}`} />
      </div>
      <div className="text-left">
        <p className={`text-sm font-medium ${enabled ? 'text-green-900' : 'text-gray-500'}`}>
          {label}
        </p>
        <p className={`text-xs ${enabled ? 'text-green-600' : 'text-gray-400'}`}>
          {enabled ? 'Ativo' : 'Inativo'}
        </p>
      </div>
    </button>
  )
}

function DiagnosticChip({ label, value }: { label: string; value?: boolean | null }) {
  const statusLabel = value == null ? 'Sem dados' : value ? 'Sim' : 'Não'
  const statusColor =
    value == null
      ? 'bg-gray-100 text-gray-600'
      : value
        ? 'bg-green-100 text-green-700'
        : 'bg-red-100 text-red-700'

  return (
    <div className="rounded-lg border border-gray-200 px-4 py-3">
      <div className="text-xs tracking-wide text-gray-500 uppercase">{label}</div>
      <div
        className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor}`}
      >
        {statusLabel}
      </div>
    </div>
  )
}

function checkGatewayCommandResult(res: any): { ok: boolean; error?: string } {
  if (res?.success === false) {
    return { ok: false, error: res.error || 'Falha na comunicação com o gateway' }
  }

  const inner = res?.result
  if (inner && typeof inner === 'object' && inner.success === false) {
    return { ok: false, error: inner.error || 'O gateway não conseguiu concluir a operação' }
  }

  return { ok: true }
}

function getRDPGatewayFeatureTone(
  status?: string | null,
): 'active' | 'inactive' | 'warning' | 'error' {
  switch (status) {
    case 'active':
      return 'active'
    case 'provisioning':
      return 'warning'
    case 'error':
      return 'error'
    default:
      return 'inactive'
  }
}

function formatGatewayDateTime(value?: string | null): string {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('pt-BR')
}
